import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SignaturePad } from "@/components/SignaturePad";
import { Plus, Check, Ban, CheckCircle2, XCircle, Clock, ImagePlus, Loader2, ClipboardList, Sliders, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (p) => `${API}/files/${p}?auth=${localStorage.getItem("nlc_token")}`;

const statusMeta = {
  pending_review: { label: "Pending Review", cls: "bg-amber-100 text-amber-700", icon: Clock },
  approved: { label: "Approved", cls: "bg-primary/10 text-primary", icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-destructive/10 text-destructive", icon: XCircle },
};

function PhotoUploader({ label, photos, onAdd, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onAdd(data.path);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <img key={i} src={fileUrl(p)} alt="" className="h-16 w-16 rounded-md object-cover border border-border" />
        ))}
        <button
          type="button"
          data-testid={testid}
          onClick={() => ref.current?.click()}
          className="h-16 w-16 rounded-md border-2 border-dashed border-border flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
    </div>
  );
}

export default function QualityChecks() {
  const { isAdmin } = useAuth();
  const [checks, setChecks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [tab, setTab] = useState("pending_review");

  // form state
  const [open, setOpen] = useState(false);
  const [jobId, setJobId] = useState("");
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState([]);
  const [signature, setSignature] = useState("");
  const [signedName, setSignedName] = useState("");
  const [notes, setNotes] = useState("");
  const [before, setBefore] = useState([]);
  const [after, setAfter] = useState([]);
  const [saving, setSaving] = useState(false);

  // review state
  const [review, setReview] = useState(null);
  const [reviewNote, setReviewNote] = useState("");

  // templates editor
  const [tmplOpen, setTmplOpen] = useState(false);
  const [tmplText, setTmplText] = useState({});
  const [tmplLabels, setTmplLabels] = useState({});
  const [requiredOver, setRequiredOver] = useState(500);

  const load = () => api.get("/quality-checks").then((r) => setChecks(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  const openNew = () => {
    setJobId(""); setTemplate(null); setResponses([]); setSignature("");
    setSignedName(""); setNotes(""); setBefore([]); setAfter([]); setOpen(true);
  };

  const chooseJob = async (id) => {
    setJobId(id);
    try {
      const { data } = await api.get(`/quality-check-template/${id}`);
      setTemplate(data);
      setResponses(data.items.map((item) => ({ item, status: "", reason: "" })));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setResp = (i, patch) => setResponses((r) => r.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const submit = async () => {
    if (!jobId) return toast.error("Select a job first.");
    if (responses.some((r) => !r.status)) return toast.error("Answer every checklist item.");
    if (responses.some((r) => r.status === "na" && !r.reason.trim())) return toast.error("Add a reason for each N/A item.");
    if (!signature) return toast.error("Signature is required.");
    if (!signedName.trim()) return toast.error("Enter the signer's name.");
    setSaving(true);
    try {
      await api.post(`/jobs/${jobId}/quality-check`, {
        responses, signature, signed_by_name: signedName, notes,
        before_photos: before, after_photos: after,
      });
      toast.success("Quality check submitted for review.");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openReview = (c) => { setReview(c); setReviewNote(c.review_note || ""); };
  const delQC = async () => {
    if (!window.confirm("Delete this quality check permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/quality-checks/${review.id}`);
      toast.success("Quality check deleted.");
      setReview(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const decide = async (action) => {
    try {
      await api.post(`/quality-checks/${review.id}/${action}`, { note: reviewNote });
      toast.success(action === "approve" ? "Approved — added to reports." : "Rejected.");
      setReview(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openTemplates = async () => {
    const { data } = await api.get("/qc-templates");
    const asText = {};
    Object.entries(data.templates).forEach(([k, v]) => (asText[k] = v.join("\n")));
    setTmplText(asText);
    setTmplLabels(data.labels);
    setRequiredOver(data.required_over ?? 500);
    setTmplOpen(true);
  };
  const saveTemplates = async () => {
    const templates = {};
    Object.entries(tmplText).forEach(([k, v]) => (templates[k] = v.split("\n").map((l) => l.trim()).filter(Boolean)));
    await api.put("/qc-templates", { templates, required_over: Number(requiredOver) || 0 });
    toast.success("Checklists updated.");
    setTmplOpen(false);
  };

  const filtered = tab === "all" ? checks : checks.filter((c) => c.status === tab);
  const counts = {
    pending_review: checks.filter((c) => c.status === "pending_review").length,
    approved: checks.filter((c) => c.status === "approved").length,
    rejected: checks.filter((c) => c.status === "rejected").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-muted-foreground">Post-job inspections. Quality checks are optional on small jobs but required to complete any job over ${requiredOver}{isAdmin ? " (set in Manage Checklists)." : "."}</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={openTemplates} data-testid="manage-checklists-btn">
              <Sliders className="h-4 w-4 mr-1.5" /> Manage Checklists
            </Button>
          )}
          <Button onClick={openNew} data-testid="new-quality-check-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New Quality Check
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="qc-tabs">
          <TabsTrigger value="pending_review" data-testid="tab-pending">Pending ({counts.pending_review})</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected ({counts.rejected})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-40" />
          No quality checks here yet.
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => {
            const m = statusMeta[c.status] || statusMeta.pending_review;
            const Icon = m.icon;
            const okCount = c.responses.filter((r) => r.status === "ok").length;
            return (
              <Card key={c.id} data-testid={`qc-card-${c.id}`} className="p-4 space-y-3 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openReview(c)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{c.job_title || c.customer_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.customer_name} · {c.service_type}</div>
                  </div>
                  <Badge className={m.cls}><Icon className="h-3 w-3 mr-1" />{m.label}</Badge>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{okCount}/{c.responses.length} checked</span>
                  <span>by {c.signed_by_name}</span>
                </div>
                {(c.before_photos?.length || c.after_photos?.length) > 0 && (
                  <div className="flex gap-1">
                    {[...(c.before_photos || []), ...(c.after_photos || [])].slice(0, 4).map((p, i) => (
                      <img key={i} src={fileUrl(p)} alt="" className="h-10 w-10 rounded object-cover border border-border" />
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* New Quality Check dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>New Lawn Quality Check</DialogTitle>
            <DialogDescription>Complete the checklist, add photos, sign and submit for owner review.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label>Job</Label>
              <Select value={jobId} onValueChange={chooseJob}>
                <SelectTrigger data-testid="qc-job-select"><SelectValue placeholder="Select a job" /></SelectTrigger>
                <SelectContent>
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>{j.title} — {j.customer_name} ({j.service_type})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {template && (
              <>
                <Badge variant="outline" className="capitalize">{template.category_label} checklist</Badge>
                <div className="space-y-2">
                  {responses.map((r, i) => (
                    <div key={i} data-testid={`qc-item-${i}`} className="rounded-md border border-border p-3 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium">{r.item}</span>
                        <div className="flex gap-1.5 shrink-0">
                          <button
                            type="button"
                            data-testid={`qc-ok-${i}`}
                            onClick={() => setResp(i, { status: "ok", reason: "" })}
                            className={`h-8 px-2.5 rounded-md text-xs font-semibold flex items-center gap-1 border transition-colors ${r.status === "ok" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary text-muted-foreground"}`}
                          >
                            <Check className="h-3.5 w-3.5" /> OK
                          </button>
                          <button
                            type="button"
                            data-testid={`qc-na-${i}`}
                            onClick={() => setResp(i, { status: "na" })}
                            className={`h-8 px-2.5 rounded-md text-xs font-semibold flex items-center gap-1 border transition-colors ${r.status === "na" ? "bg-amber-500 text-white border-amber-500" : "border-border hover:border-amber-500 text-muted-foreground"}`}
                          >
                            <Ban className="h-3.5 w-3.5" /> N/A
                          </button>
                        </div>
                      </div>
                      {r.status === "na" && (
                        <Textarea
                          data-testid={`qc-reason-${i}`}
                          value={r.reason}
                          onChange={(e) => setResp(i, { reason: e.target.value })}
                          placeholder="Why couldn't this be completed / checked?"
                          className="text-sm"
                          rows={2}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <PhotoUploader label="Before Photos" photos={before} onAdd={(p) => setBefore((x) => [...x, p])} testid="add-before-photo" />
                  <PhotoUploader label="After Photos" photos={after} onAdd={(p) => setAfter((x) => [...x, p])} testid="add-after-photo" />
                </div>

                <div className="space-y-1.5">
                  <Label>Notes (optional)</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Any extra notes for the owner" data-testid="qc-notes" />
                </div>

                <div className="space-y-1.5">
                  <Label>Signature *</Label>
                  <SignaturePad onChange={setSignature} />
                </div>
                <div className="space-y-1.5">
                  <Label>Signed by (name) *</Label>
                  <Input value={signedName} onChange={(e) => setSignedName(e.target.value)} placeholder="Crew manager name" data-testid="qc-signed-name" />
                </div>
                <p className="text-xs text-muted-foreground">Dated automatically on submit: {new Date().toLocaleString()}</p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!template || saving} data-testid="qc-submit-btn">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Submit for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          {review && (
            <>
              <DialogHeader><DialogTitle>{review.job_title || review.customer_name}</DialogTitle>
                <DialogDescription>Quality check details, photos and signature.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="flex items-center gap-2">
                  <Badge className={(statusMeta[review.status] || statusMeta.pending_review).cls}>
                    {(statusMeta[review.status] || statusMeta.pending_review).label}
                  </Badge>
                  <span className="text-muted-foreground">{review.customer_name} · {review.service_type}</span>
                </div>
                <div className="space-y-1.5">
                  {review.responses.map((r, i) => (
                    <div key={i} className="flex items-start gap-2">
                      {r.status === "ok"
                        ? <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        : <Ban className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}
                      <div>
                        <span className={r.status === "na" ? "text-muted-foreground" : ""}>{r.item}</span>
                        {r.status === "na" && <div className="text-xs text-amber-600">N/A — {r.reason}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                {(review.before_photos?.length > 0 || review.after_photos?.length > 0) && (
                  <div className="grid grid-cols-2 gap-3">
                    {review.before_photos?.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground mb-1">Before</div>
                        <div className="flex flex-wrap gap-1.5">
                          {review.before_photos.map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="h-20 w-20 rounded object-cover border" />)}
                        </div>
                      </div>
                    )}
                    {review.after_photos?.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground mb-1">After</div>
                        <div className="flex flex-wrap gap-1.5">
                          {review.after_photos.map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="h-20 w-20 rounded object-cover border" />)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {review.notes && <div><span className="font-semibold">Notes: </span>{review.notes}</div>}
                <div>
                  <div className="text-xs font-semibold text-muted-foreground mb-1">Signed by {review.signed_by_name} · {new Date(review.signed_at).toLocaleString()}</div>
                  {review.signature && <img src={review.signature} alt="signature" className="h-24 rounded border bg-white" />}
                </div>
                {isAdmin && review.status === "pending_review" && (
                  <div className="space-y-2 pt-2 border-t">
                    <Label>Review note (optional)</Label>
                    <Textarea value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} rows={2} data-testid="review-note" />
                  </div>
                )}
                {review.status !== "pending_review" && review.review_note && (
                  <div className="text-xs text-muted-foreground">Review note: {review.review_note} — {review.approved_by}</div>
                )}
              </div>
              {isAdmin && (
                <DialogFooter className="gap-2 sm:justify-between">
                  <Button variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={delQC} data-testid="delete-qc-btn">
                    <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                  </Button>
                  {review.status === "pending_review" && (
                    <div className="flex gap-2">
                      <Button variant="outline" className="text-destructive" onClick={() => decide("reject")} data-testid="reject-btn">
                        <XCircle className="h-4 w-4 mr-1.5" /> Reject
                      </Button>
                      <Button onClick={() => decide("approve")} data-testid="approve-btn">
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
                      </Button>
                    </div>
                  )}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Templates editor */}
      <Dialog open={tmplOpen} onOpenChange={setTmplOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Manage Checklists</DialogTitle>
            <DialogDescription>Edit the default quality-check items applied per service type.</DialogDescription>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">One item per line. These auto-apply to jobs by service type.</p>
          <div className="rounded-md border p-3 space-y-1.5">
            <Label className="flex items-center gap-2">Require a quality check on jobs over ($)</Label>
            <Input type="number" data-testid="qc-threshold-input" value={requiredOver} onChange={(e) => setRequiredOver(e.target.value)} className="w-40" />
            <p className="text-[0.7rem] text-muted-foreground">Jobs at or below this price can be completed without a quality check. Set to 0 to require one on every job.</p>
          </div>
          <div className="space-y-4">
            {Object.keys(tmplText).map((k) => (
              <div key={k} className="space-y-1.5">
                <Label>{tmplLabels[k] || k}</Label>
                <Textarea
                  data-testid={`tmpl-${k}`}
                  value={tmplText[k]}
                  onChange={(e) => setTmplText((t) => ({ ...t, [k]: e.target.value }))}
                  rows={6}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTmplOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplates} data-testid="save-templates-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
